import React, { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { getCurrentUser, getToken } from './context/CommunicationProvider/authUser';
import SiteLayout from './components/SiteLayout';
import pages, { Page, PagePathname } from './pages';
import Login from './sections/auth/Login';
import Page404 from './pages/Page404/Page404';

interface Props {
  setUserToken: React.Dispatch<React.SetStateAction<string>>;
  setApolloClientToken?: () => void;
}

function RequireAuth({ children, setApolloClientToken }: { children: JSX.Element; setApolloClientToken?: () => void }) {
  if (!getCurrentUser(setApolloClientToken)) {
    return <Navigate to="/login" replace />;
  }
  return <SiteLayout main={children} />;
}

function Router({ setUserToken, setApolloClientToken }: Props) {
  useEffect(() => {
    const token = getToken();
    if (token) {
      setUserToken(token);
    }
  }, []);

  // Define wrappers inside render but they invoke stable components or logic.
  // Actually, to fully satisfy the linter, we should ideally simply use 'element' or define components outside.
  // However, these wrappers depend on 'setApolloClientToken'.
  // Using 'Component={() => ...}' is still technically an unstable component if the arrow function is new every time.
  // But 'Component' prop in Route (v6) prefers a component *type*.
  // Let's try to use 'element' which is the standard way in v6 and avoids this issue entirely.

  return (
    <Routes>
      <Route path="/" element={<Navigate to={PagePathname.dashboard} replace />} />
      {pages.map((item: Page) => (
        <Route
          key={item.path}
          path={item.path}
          element={<RequireAuth setApolloClientToken={setApolloClientToken}>{item.render}</RequireAuth>}
        />
      ))}

      <Route
        path="login"
        element={getCurrentUser(setApolloClientToken) ? <Navigate to={PagePathname.dashboard} replace /> : <Login />}
      />
      <Route path="404" element={<Page404 />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}

export default Router;
